<template>
    <div class="blank-widget-setting">
        <div class="setting-content">
            <!-- 위젯 색상 설정 섹션 -->
            <div class="setting-section">
                <h5>{{ $t("위젯 설정") }}</h5>
                <!-- 위젯 색상 -->
                <div class="form-group">
                    <label>{{ $t("위젯 색상") }}</label>
                    <EnergyColorPalette v-model="backgroundColor" :energyType="'general'" />
                </div>

                <!-- 색상 미리보기 -->
                <div class="form-group">
                    <label>{{ $t("색상 미리보기") }}</label>
                    <div class="color-preview" :style="getPreviewStyle()">
                        <div class="preview-content">
                            <i class="fas fa-plus-circle"></i>
                            <span>{{ $t("빈 위젯") }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 버튼 영역 -->
        <div class="setting-actions">
            <button type="button" class="btn btn-secondary" @click="$emit('on-modal-cancel')">
                {{ $t("취소") }}
            </button>
            <button type="button" class="btn btn-primary" @click="onSave">
                {{ $t("저장") }}
            </button>
        </div>
    </div>
</template>

<script>
import EnergyColorPalette from "@components/colorpalette/EnergyColorPalette.vue";

export default {
    name: "BlankWidgetSetting",
    components: {
        EnergyColorPalette,
    },
    props: {
        initSetting: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            widgetTitle: "",
            backgroundColor: "#1a1a2e",
        };
    },
    mounted() {
        this.initData();
    },
    methods: {
        initData() {
            // 기존 설정값으로 초기화
            if (this.initSetting) {
                this.widgetTitle = this.initSetting.title || "";
                this.backgroundColor = this.initSetting.panel?.backgroundColor || "#1a1a2e";
            } else {
                // 새로운 위젯 구성 시 기본값으로 초기화
                this.widgetTitle = "";
                this.backgroundColor = "#1a1a2e";
            }
        },

        getPreviewStyle() {
            // 색상을 그라데이션으로 변환
            if (this.backgroundColor && this.backgroundColor.startsWith("#")) {
                const darkerColor = this.adjustColor(this.backgroundColor, -30);
                const darkestColor = this.adjustColor(this.backgroundColor, -60);

                return {
                    background: `linear-gradient(45deg, ${darkestColor} 0%, ${darkerColor} 85%, ${this.backgroundColor} 100%)`,
                };
            } else {
                return {
                    background: this.backgroundColor || "#1a1a2e",
                };
            }
        },

        adjustColor(color, amount) {
            // 색상을 밝게 하거나 어둡게 조정하는 헬퍼 함수
            const hex = color.replace("#", "");
            const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
            const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
            const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
            return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b
                .toString(16)
                .padStart(2, "0")}`;
        },

        onSave() {
            const setting = {
                title: this.widgetTitle || this.$t("빈 위젯"),
                panel: {
                    backgroundColor: this.backgroundColor,
                },
            };

            // 설정이 변경되었음을 부모 컴포넌트에 알림
            this.$emit("on-modal-ok", setting);
        },
    },
};
</script>

<style scoped>
.blank-widget-setting {
    padding: 20px;
}

.setting-content {
    margin-bottom: 20px;
}

.setting-section {
    margin-bottom: 30px;
    padding: 20px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #f8f9fa;
}

.setting-section h5 {
    margin-bottom: 20px;
    color: #333;
    font-weight: 600;
    border-bottom: 2px solid #007bff;
    padding-bottom: 8px;
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #555;
}

.form-control {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    transition: border-color 0.3s ease;
}

.form-control:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.color-preview {
    width: 100%;
    height: 120px;
    border-radius: 8px;
    border: 2px solid #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
}

.color-preview::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(52, 152, 219, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(46, 204, 113, 0.08) 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, rgba(155, 89, 182, 0.05) 0%, transparent 50%);
    pointer-events: none;
}

.preview-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: rgba(255, 255, 255, 0.8);
    text-align: center;
    z-index: 1;
}

.preview-content i {
    font-size: 24px;
    opacity: 0.7;
}

.preview-content span {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.3px;
}

.setting-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 20px;
    border-top: 1px solid #e0e0e0;
}

.btn {
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 600;
    transition: all 0.3s ease;
    border: none;
    cursor: pointer;
}

.btn-primary {
    background-color: #007bff;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background-color: #0056b3;
}

.btn-primary:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
}

.btn-secondary {
    background-color: #6c757d;
    color: white;
}

.btn-secondary:hover {
    background-color: #545b62;
}
</style>
